

import { useState } from "react";
import { SectionCard } from "./SectionCard";
import { Financials, ClientPaymentRow, fmt, fmtDate } from "./types";

interface RevenueSectionProps {
  financials: Financials;
  clientPayments: ClientPaymentRow[];
}

export function RevenueSection({ financials, clientPayments }: RevenueSectionProps) {
  const f = financials;
  const [isOpen, setIsOpen] = useState(true);

  const summaryItems = [
    { label: "Invoice Total", value: fmt(f.income_subtotal), color: "text-stone-800" },
    { label: "Discount", value: `- ${fmt(f.discount_amount)}`, color: "text-rose-600" },
    { label: "Net Revenue", value: fmt(f.net_revenue), color: "text-stone-800 font-bold" },
    { label: "Client Paid", value: fmt(f.customer?.paid ?? 0), color: "text-emerald-600 font-bold" },
    {
      label: "Balance Due",
      value: fmt(f.customer?.balance ?? 0),
      color: (f.customer?.balance ?? 0) > 0 ? "text-rose-600 font-bold" : "text-emerald-600 font-bold",
    },
  ];

  return (
    <SectionCard
      title="Revenue & Client Payments"
      icon={
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      }
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-4 sm:mb-5">
        {summaryItems.map(({ label, value, color }) => (
          <div key={label} className="bg-stone-50 rounded-xl p-2 sm:p-3 border border-stone-100">
            <div className={`text-sm sm:text-base ${color} truncate`}>{value}</div>
            <div className="text-xs text-stone-400 mt-0.5 truncate">{label}</div>
          </div>
        ))}
      </div>

      {/* Client payments table */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center gap-2 text-xs font-semibold text-stone-500 hover:text-stone-800 mb-3 transition-colors w-full"
      >
        <svg className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${isOpen ? "rotate-90" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        <span className="truncate">{clientPayments.length} Client Payment{clientPayments.length !== 1 ? "s" : ""}</span>
      </button>

      {isOpen && clientPayments.length > 0 && (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="min-w-[500px] px-4 sm:px-0">
            <div className="rounded-xl border border-stone-100 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-stone-50">
                  <tr>
                    {["Date", "Method", "Reference", "Notes", "Amount"].map((h) => (
                      <th key={h} className="text-left px-3 py-2 text-stone-500 font-semibold uppercase whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-50">
                  {clientPayments.map((p) => (
                    <tr key={p.id} className="hover:bg-stone-50/50">
                      <td className="px-3 py-2.5 text-stone-600 whitespace-nowrap">{fmtDate(p.payment_date)}</td>
                      <td className="px-3 py-2.5 text-stone-600 capitalize">{p.payment_method ?? "-"}</td>
                      <td className="px-3 py-2.5 text-stone-500 max-w-[100px] truncate">{p.reference_number ?? "-"}</td>
                      <td className="px-3 py-2.5 text-stone-400 max-w-[150px] truncate">{p.notes ?? "-"}</td>
                      <td className="px-3 py-2.5 font-semibold text-emerald-700 text-right whitespace-nowrap">{fmt(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {isOpen && clientPayments.length === 0 && (
        <p className="text-xs text-stone-400 italic">No client payments recorded yet.</p>
      )}
    </SectionCard>
  );
}
